import React from 'react';

import { ActivityCards } from './activity_cards';
import { ContactCards } from './contact_cards';
import { ContactListCards } from './contact_list_cards';
import { DealCards } from './deal_cards';
import { EmailCards } from './email_cards';
import { PropertyCards } from './property_cards';
import { QuestionCards } from './question_cards';
import { RecommendationSteps } from './recommendation_steps';
import { TaskCards } from './task_cards';
import { VoucherCards } from './voucher_cards';

import type { InlineToolCall, ToolCall } from '../types';

type InlineTool = React.ComponentType<{ superseded: boolean; toolCalls: InlineToolCall[] }>;

type InlineToolProps<Name extends InlineToolCall['name']> = {
  superseded: boolean;
  toolCalls: Extract<InlineToolCall, { name: Name }>[];
};

type InlineToolComponent<Name extends InlineToolCall['name']> = React.ComponentType<InlineToolProps<Name>> & {
  // Set when a card renders its own buttons for actions, to prevent the generic ones from rendering
  claimsActions?: boolean
};

const NoCard = () => null;

const toolsByName: { [Name in InlineToolCall['name']]: InlineToolComponent<Name> } = {
  activity_search: ActivityCards,
  add_contacts_to_list: ContactListCards,
  ask_user_questions: QuestionCards,
  contact_load: ContactCards,
  contact_search: ContactCards,
  create_contact: ContactCards,
  create_deal: DealCards,
  deal_load: DealCards,
  deal_search: DealCards,
  offer_next_step: NoCard,
  property_load: PropertyCards,
  property_search: PropertyCards,
  recommendations: RecommendationSteps,
  send_email: EmailCards,
  task_load: TaskCards,
  task_search: TaskCards,
  update_contact: ContactCards,
  voucher_load: VoucherCards,
  voucher_search: VoucherCards,
};

export function isInlineToolCall(toolCall: ToolCall): toolCall is InlineToolCall {
  return toolCall.name in toolsByName;
}

// Inline tools render as part of the transcript instead of interrupting the user, so they never
// reach the standalone queue - the message they arrived with owns them.
export function InlineTools({ superseded, toolCalls }: { superseded: boolean; toolCalls: InlineToolCall[] }) {
  const groups = getToolGroups(toolCalls);
  if (!groups.length) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2 mb-2">
      {groups.map(([Tool, groupedCalls], index) => (
        <Tool key={index} superseded={superseded} toolCalls={groupedCalls} />
      ))}
    </div>
  );
}

function getToolGroups(toolCalls: InlineToolCall[]) {
  const groups = new Map<InlineTool, InlineToolCall[]>();

  toolCalls.forEach((toolCall) => {
    const tool = toolsByName[toolCall.name] as InlineTool;
    if (tool !== NoCard) {
      groups.set(tool, [...(groups.get(tool) || []), toolCall]);
    }
  });

  return [...groups];
}

export function getClaimedActionIds(toolCalls: InlineToolCall[]): number[] {
  return toolCalls
    .filter(toolCall => toolsByName[toolCall.name].claimsActions)
    .map(toolCall => ('actionId' in toolCall.metadata ? toolCall.metadata.actionId : null))
    .filter((actionId): actionId is number => actionId !== null);
}
